import { useMemo } from 'react';
import { Tabs, type TabItem } from '@garnet/ui';
import { useHashRoute } from './router.js';
import { FixtureApi } from './data/FixtureApi.js';
import type { AdminApi } from './data/types.js';
import { Sources } from './screens/Sources.js';
import { Rules } from './screens/Rules.js';
import { Inspectors } from './screens/Inspectors.js';
import { Customers } from './screens/Customers.js';
import { Webhooks } from './screens/Webhooks.js';

const TABS = ['sources', 'rules', 'inspectors', 'customers', 'webhooks'] as const;
type Tab = (typeof TABS)[number];

const TAB_ITEMS: TabItem<Tab>[] = [
  { key: 'sources', label: 'Sources' },
  { key: 'rules', label: 'Rules' },
  { key: 'inspectors', label: 'Inspector ops' },
  { key: 'customers', label: 'Customer ops' },
  { key: 'webhooks', label: 'Webhooks' },
];

export function App({ api = new FixtureApi() as AdminApi }: { api?: AdminApi } = {}) {
  const [tab, nav] = useHashRoute<Tab>(TABS, 'sources');
  const ctx = useMemo(() => api, [api]);

  return (
    <div style={{ background: 'var(--bg-base)', minHeight: '100vh', color: 'var(--fg-1)', fontFamily: 'var(--font-body)' }}>
      <header
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--bg-elevated)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--garnet-text)' }}>
              Garnet
            </span>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Platform admin</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>internal · ops</span>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <Tabs tabs={TAB_ITEMS} active={tab} onChange={nav} ariaLabel="Admin sections" />
          </div>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: '0 auto', padding: '20px 24px 64px' }}>
        {tab === 'sources' && <Sources api={ctx} />}
        {tab === 'rules' && <Rules api={ctx} />}
        {tab === 'inspectors' && <Inspectors api={ctx} />}
        {tab === 'customers' && <Customers api={ctx} />}
        {tab === 'webhooks' && <Webhooks api={ctx} />}
      </main>
    </div>
  );
}
